import React, { useState, useEffect } from 'react';
import { Upload, Save, X, Plus, Trash2, Edit3, FileText } from 'lucide-react';
import {
  ClientData,
  PageContent,
  ContentTopic,
  HeroContent,
  HighlightContent,
  InsightCardContent,
  DemographicsData,
  PersonalValue,
  InsightIndexEntry,
  BrandAffinityData,
  Audience,
} from '../types';
import FileUpload from './FileUpload';
import RichTextEditor, { toRichTextHtml } from './RichTextEditor';
import ClientService from '../services/clientService';
import { supabase } from '../lib/supabase';

const normalizeKey = (value: string | null | undefined) =>
  (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const extractNumber = (input: any, priorityKeys: string[] = []): number | undefined => {
  if (typeof input === 'number' && Number.isFinite(input)) return input;
  if (typeof input === 'string') {
    const match = input.replace(/,/g, '').match(/-?\d+(\.\d+)?/);
    if (match) return Number(match[0]);
    return undefined;
  }
  if (input && typeof input === 'object') {
    for (const key of priorityKeys) {
      const val = (input as any)[key];
      const parsed = extractNumber(val, []);
      if (parsed !== undefined) return parsed;
    }
    // fallback search common fields
    for (const candidate of ['value', 'percent', 'percentage', 'composition', 'score', 'index']) {
      if (candidate === 'index') continue; // avoid infinite loop
      if (Object.prototype.hasOwnProperty.call(input, candidate)) {
        const parsed = extractNumber((input as any)[candidate], []);
        if (parsed !== undefined) return parsed;
      }
    }
  }
  return undefined;
};

const findValueWithAliases = (source: any, aliases: string[]): any => {
  if (!source || typeof source !== 'object') return undefined;
  const entries = Object.entries(source);
  for (const alias of aliases) {
    const normalized = normalizeKey(alias);
    const hit = entries.find(([key]) => normalizeKey(key) === normalized);
    if (hit) return hit[1];
  }
  return undefined;
};

const createDefaultDemographics = (): DemographicsData => ({
  targetableIds: '0',
  gender: { male: 0, female: 0 },
  ageGroups: {
    '18-24': 0,
    '25-34': 0,
    '35-44': 0,
    '45-49': 0,
    '50-54': 0,
    '55-64': 0,
    '65+': 0,
  },
  income: {
    '<$25k': 0,
    '$25-50k': 0,
    '$50-75k': 0,
    '$75-100k': 0,
    '$100-150k': 0,
    '$150-200k': 0,
    '$200-250k': 0,
    '$250k+': 0,
  },
  employment: {
    fullTime: 0,
    selfEmployed: 0,
    partTime: 0,
    unemployed: 0,
    homemaker: 0,
    student: 0,
    compositionPrimary: 0,
    retired: 0,
    index: 100,
  },
  education: {
    lessThanHighSchool: 0,
    highSchool: 0,
    someCollege: 0,
    collegeDegree: 0,
    postGrad: 0,
  },
  maritalStatus: {
    single: 0,
    married: 0,
    divorced: 0,
    widowed: 0,
  },
  children: {
    none: 0,
    one: 0,
    twoOrThree: 0,
    fourOrFive: 0,
    sixPlus: 0,
  },
});

type DemographicsFormState = {
  targetableIds: string;
  gender: {
    male: string;
    female: string;
  };
  ageGroups: Record<keyof DemographicsData['ageGroups'], string>;
  education: Record<keyof DemographicsData['education'], string>;
  employment: {
    fullTime: string;
    selfEmployed: string;
    partTime: string;
    unemployed: string;
    homemaker: string;
    student: string;
    retired: string;
    index: string;
    compositionPrimary: string;
  };
};

const formatNumberInput = (value: number | undefined | null): string =>
  value === undefined || value === null ? '' : String(value);

const createDemographicsFormState = (data?: DemographicsData | null): DemographicsFormState => ({
  targetableIds: data?.targetableIds ? String(data.targetableIds) : '',
  gender: {
    male: formatNumberInput(data?.gender.male),
    female: formatNumberInput(data?.gender.female),
  },
  ageGroups: {
    '18-24': formatNumberInput(data?.ageGroups['18-24']),
    '25-34': formatNumberInput(data?.ageGroups['25-34']),
    '35-44': formatNumberInput(data?.ageGroups['35-44']),
    '45-49': formatNumberInput(data?.ageGroups['45-49']),
    '50-54': formatNumberInput(data?.ageGroups['50-54']),
    '55-64': formatNumberInput(data?.ageGroups['55-64']),
    '65+': formatNumberInput(data?.ageGroups['65+']),
  },
  education: {
    lessThanHighSchool: formatNumberInput(data?.education.lessThanHighSchool),
    highSchool: formatNumberInput(data?.education.highSchool),
    someCollege: formatNumberInput(data?.education.someCollege),
    collegeDegree: formatNumberInput(data?.education.collegeDegree),
    postGrad: formatNumberInput(data?.education.postGrad),
  },
  employment: {
    fullTime: formatNumberInput(data?.employment.fullTime),
    selfEmployed: formatNumberInput(data?.employment.selfEmployed),
    partTime: formatNumberInput(data?.employment.partTime),
    unemployed: formatNumberInput(data?.employment.unemployed),
    homemaker: formatNumberInput(data?.employment.homemaker),
    student: formatNumberInput(data?.employment.student),
    retired: formatNumberInput(data?.employment.retired),
    index: formatNumberInput(data?.employment.index),
    compositionPrimary: formatNumberInput(data?.employment.compositionPrimary),
  },
});

const DEMO_AGE_KEYS: Array<keyof DemographicsData['ageGroups']> = [
  '18-24',
  '25-34',
  '35-44',
  '45-49',
  '50-54',
  '55-64',
  '65+',
];

const DEMO_EDUCATION_KEYS: Array<{ key: keyof DemographicsData['education']; label: string }> = [
  { key: 'lessThanHighSchool', label: 'Less Than High School' },
  { key: 'highSchool', label: 'High School Degree' },
  { key: 'someCollege', label: 'Some College' },
  { key: 'collegeDegree', label: 'College Degree' },
  { key: 'postGrad', label: 'Post-grad Degree' },
];

const DEMO_EMPLOYMENT_FIELDS = [
  { key: 'fullTime', label: 'Full-Time %' },
  { key: 'selfEmployed', label: 'Self-Employed %' },
  { key: 'partTime', label: 'Part-Time %' },
  { key: 'unemployed', label: 'Unemployed %' },
  { key: 'homemaker', label: 'Homemaker %' },
  { key: 'student', label: 'Student %' },
  { key: 'retired', label: 'Retired %' },
  { key: 'index', label: 'Index' },
  { key: 'compositionPrimary', label: '% Composition (Primary Role)' },
] as const;

const PERSONAL_VALUE_FIELDS: Array<{
  label: string;
  category: PersonalValue['category'];
}> = [
  { label: 'Tolerance', category: 'self-transcendence' },
  { label: 'Nature', category: 'self-transcendence' },
  { label: 'Equality', category: 'self-transcendence' },
  { label: 'Dependability', category: 'self-transcendence' },
  { label: 'Caring', category: 'self-transcendence' },
  { label: 'Humility', category: 'self-transcendence' },
  { label: 'Conformity', category: 'conservation' },
  { label: 'Duty', category: 'conservation' },
  { label: 'Tradition', category: 'conservation' },
  { label: 'Security', category: 'conservation' },
  { label: 'Safety', category: 'conservation' },
  { label: 'Reputation', category: 'self-enhancement' },
  { label: 'Influence', category: 'self-enhancement' },
  { label: 'Authority', category: 'self-enhancement' },
  { label: 'Achievement', category: 'self-enhancement' },
  { label: 'Pleasure', category: 'openness' },
  { label: 'Stimulation', category: 'openness' },
  { label: 'Independence', category: 'openness' },
  { label: 'Creativity', category: 'openness' },
];

type PersonalValuesFormState = Record<string, string>;

const createPersonalValuesFormState = (values?: PersonalValue[]): PersonalValuesFormState => {
  const byName = new Map<string, PersonalValue>();
  (values || []).forEach((item) => {
    if (!item?.name) return;
    byName.set(normalizeKey(item.name), item);
  });

  return PERSONAL_VALUE_FIELDS.reduce((acc, field) => {
    const existing = byName.get(normalizeKey(field.label));
    acc[field.label] = formatNumberInput(existing?.value);
    return acc;
  }, {} as PersonalValuesFormState);
};

const parseNumericInput = (value: string): number => {
  if (!value) return 0;
  const cleaned = value.replace(/[^0-9.\-]/g, '');
  if (!cleaned) return 0;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatTargetableIds = (value: any, fallback: string): string => {
  if (typeof value === 'string' && value.trim()) return value.trim();
  const numeric = extractNumber(value);
  if (typeof numeric === 'number') {
    if (numeric >= 1000000) return `${Math.round(numeric / 100000) / 10}M`;
    if (numeric >= 1000) return `${Math.round(numeric / 100)}K`;
    return String(Math.round(numeric));
  }
  return fallback;
};

const updateGroupFromSource = (
  target: Record<string, number>,
  source: any,
  aliasMap: Record<string, string[]> = {}
) => {
  if (!source || typeof source !== 'object') return;
  Object.keys(target).forEach((key) => {
    const aliases = [key, ...(aliasMap[key] || [])].map((alias) => normalizeKey(alias));
    let raw: any;
    for (const alias of aliases) {
      raw = findValueWithAliases(source, [alias]);
      if (raw !== undefined) break;
    }
    const parsed = extractNumber(raw);
    if (typeof parsed === 'number') {
      target[key] = parsed;
    }
  });
};

const mergeEmploymentFromSource = (
  target: DemographicsData['employment'],
  source: any
) => {
  if (!source) return;
  const normalizeKey = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const entries: Array<{ label: string; value: any }> = [];

  if (Array.isArray(source)) {
    source.forEach((item) => {
      if (!item) return;
      const label = item.label ?? item.name ?? item.title;
      if (!label) return;
      entries.push({ label, value: item });
    });
  } else if (typeof source === 'object') {
    Object.entries(source).forEach(([label, value]) => {
      entries.push({ label, value });
    });
  }

  entries.forEach(({ label, value }) => {
    const key = normalizeKey(String(label));
    const composition =
      extractNumber((value as any)?.composition, ['value', 'percent', 'percentage']) ??
      extractNumber(value, ['value', 'percent', 'percentage']);
    const index =
      extractNumber((value as any)?.index, ['index', 'score']) ??
      (typeof value === 'object' ? extractNumber(value, ['index', 'score']) : undefined);

    if (key.includes('self') && key.includes('employ')) {
      if (typeof composition === 'number') target.selfEmployed = composition;
      if (typeof index === 'number') target.selfEmployedIndex = index;
    } else if (key.includes('full') && key.includes('time')) {
      if (typeof composition === 'number') target.fullTime = composition;
      if (typeof index === 'number') {
        target.fullTimeIndex = index;
        target.index = index;
      }
    } else if (key.includes('part') && key.includes('time')) {
      if (typeof composition === 'number') target.partTime = composition;
    } else if (key.includes('unemploy')) {
      if (typeof composition === 'number') target.unemployed = composition;
    } else if (key.includes('homemaker')) {
      if (typeof composition === 'number') target.homemaker = composition;
    } else if (key.includes('student')) {
      if (typeof composition === 'number') target.student = composition;
    } else if (key.includes('retire')) {
      if (typeof composition === 'number') target.retired = composition;
    } else if (key === 'index' && typeof index === 'number') {
      target.index = index;
    }
  });
};

const deriveDemographicsFromAudienceSection = (
  section: any,
  existing?: DemographicsData | null
): DemographicsData | null => {
  if (!section || typeof section !== 'object') return existing ?? null;
  const audienceData = section.data || {};
  if (!audienceData || typeof audienceData !== 'object') return existing ?? null;

  const demographics = existing
    ? JSON.parse(JSON.stringify(existing)) as DemographicsData
    : createDefaultDemographics();

  const genderData =
    findValueWithAliases(audienceData, ['gender', 'genderbreakdown', 'gender composition']) ||
    audienceData.gender ||
    audienceData.Gender;
  updateGroupFromSource(demographics.gender, genderData, {
    male: ['men'],
    female: ['women'],
  });

  const ageData =
    findValueWithAliases(audienceData, ['age', 'agegroups', 'agegroup', 'age breakdown']) ||
    audienceData.age ||
    audienceData.Age;
  updateGroupFromSource(demographics.ageGroups, ageData);

  const incomeData =
    findValueWithAliases(audienceData, ['income', 'householdincome', 'incomelevels']) ||
    audienceData.income ||
    audienceData.Income;
  updateGroupFromSource(demographics.income, incomeData, {
    '<$25k': ['<$25k', '< $25k', 'under$25k', 'under 25k'],
    '$25-50k': ['$25k-50k', '$25-50k', '25-50k'],
    '$50-75k': ['$50k-75k', '$50-75k', '50-75k'],
    '$75-100k': ['$75k-100k', '$75-100k', '75-100k'],
    '$100-150k': ['$100k-150k', '$100-150k', '100-150k'],
    '$150-200k': ['$150k-200k', '$150-200k', '150-200k'],
    '$200-250k': ['$200k-250k', '$200-250k', '200-250k'],
    '$250k+': ['$250k+', '$250k plus', '250k+'],
  });

  const educationData =
    findValueWithAliases(audienceData, ['education', 'educationlevel', 'education levels']) ||
    audienceData.education ||
    audienceData.Education;
  if (educationData && typeof educationData === 'object') {
    const map: Array<[keyof DemographicsData['education'], string[]]> = [
      ['lessThanHighSchool', ['less than high school', 'lt high school']],
      ['highSchool', ['high school degree', 'hs degree', 'high school']],
      ['someCollege', ['some college']],
      ['collegeDegree', ['college degree', 'college']],
      ['postGrad', ['post-grad degree', 'post grad', 'postgraduate']],
    ];
    map.forEach(([key, aliases]) => {
      const raw = findValueWithAliases(educationData, [key, ...aliases]);
      const parsed = extractNumber(raw);
      if (typeof parsed === 'number') {
        demographics.education[key] = parsed;
      }
    });
  }

  const maritalData =
    findValueWithAliases(audienceData, ['marital status', 'marital', 'relationship status']) ||
    audienceData.marital ||
    audienceData.Marital;
  updateGroupFromSource(demographics.maritalStatus, maritalData, {
    single: ['single', 'never married'],
    married: ['married'],
    divorced: ['divorced'],
    widowed: ['widow', 'widowed'],
  });

  const childrenData =
    findValueWithAliases(audienceData, ['children', 'household children']) ||
    audienceData.children ||
    audienceData.Children;
  updateGroupFromSource(demographics.children, childrenData, {
    none: ['no children', 'none'],
    one: ['1 child', 'one child'],
    twoOrThree: ['2 or 3 children', '2 children', '3 children'],
    fourOrFive: ['4 or 5 children', '4 children', '5 children'],
    sixPlus: ['6+ children', '6 or more children'],
  });

  const employmentData =
    findValueWithAliases(audienceData, ['employment status', 'employment', 'occupation']) ||
    audienceData.employment ||
    audienceData.Employment;
  mergeEmploymentFromSource(demographics.employment, employmentData);

  const metrics = section.metrics || {};
  const targetableRaw =
    findValueWithAliases(metrics, ['projectedadultpopulation', 'targetableids']) ??
    findValueWithAliases(audienceData, ['targetableids', 'audiencesize']);
  if (targetableRaw !== undefined) {
    demographics.targetableIds = formatTargetableIds(targetableRaw, demographics.targetableIds);
  }

  return demographics;
};

const coerceArray = (value: any): any[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      return coerceArray(parsed);
    } catch {
      return [];
    }
  }
  if (Array.isArray(value?.values)) return value.values;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.rows)) return value.rows;
  if (typeof value === 'object') {
    const entries = Object.entries(value);
    if (entries.every(([, val]) => typeof val === 'number')) {
      return entries.map(([label, val]) => ({ label, value: val }));
    }
  }
  return [];
};

const normalizeValueCategory = (raw: any, fallback?: PersonalValue['category']): PersonalValue['category'] => {
  if (typeof raw === 'string') {
    const cleaned = raw.toLowerCase().replace(/[^a-z-]/g, '');
    if (cleaned.includes('openness')) return 'openness';
    if (cleaned.includes('transcend')) return 'self-transcendence';
    if (cleaned.includes('enhancement')) return 'self-enhancement';
    if (cleaned.includes('conservation')) return 'conservation';
  }
  return fallback ?? 'openness';
};

const pickFirstString = (...candidates: any[]): string | undefined => {
  for (const candidate of candidates) {
    if (typeof candidate === 'string') {
      const trimmed = candidate.trim();
      if (trimmed) return trimmed;
    }
  }
  return undefined;
};

const derivePersonalValuesFromSection = (
  section: any,
  existing?: PersonalValue[]
): PersonalValue[] | null => {
  const seen = new Map<string, PersonalValue>();

  const pushValue = (
    name: string,
    value: number,
    categoryHint?: any,
    details?: Record<string, any>
  ) => {
    if (!name || typeof value !== 'number' || Number.isNaN(value)) return;
    const key = normalizeKey(name);
    const existingCategory =
      existing?.find((pv) => normalizeKey(pv.name) === key)?.category;
    const existingEntry = seen.get(key);
    const category =
      existingEntry?.category ?? normalizeValueCategory(categoryHint, existingCategory);
    const descriptor = pickFirstString(
      details?.descriptor,
      details?.descriptorTitle,
      details?.descriptive_title,
      details?.subtitle,
      details?.subTitle,
      details?.heading,
      details?.longTitle,
      details?.long_title
    );
    const description = pickFirstString(
      details?.description,
      details?.summary,
      details?.narrative,
      details?.detail,
      details?.body,
      details?.text
    );
    const tip = pickFirstString(
      details?.tip,
      details?.recommendation,
      details?.guidance,
      details?.takeaway,
      details?.keyTakeaway,
      details?.key_takeaway,
      details?.insight
    );

    const next: PersonalValue = existingEntry ? { ...existingEntry } : { name, value, category };
    // Treat each upload as authoritative; do not retain higher stale values from prior runs.
    next.value = value;
    if (!next.longTitle && descriptor) next.longTitle = descriptor;
    if (!next.description && description) next.description = description;
    if (!next.tip && tip) next.tip = tip;

    seen.set(key, next);
  };

  const visit = (node: any, categoryHint?: any) => {
    if (node === null || node === undefined) return;

    if (Array.isArray(node)) {
      node.forEach((child) => visit(child, categoryHint));
      return;
    }

    if (typeof node !== 'object') return;

    const name = node.name || node.label || node.valueLabel || node.title;
    const value = extractNumber(
      node.value ?? node.score ?? node.index ?? node.percentage ?? node.percent ?? node.amount
    );

    if (name && typeof value === 'number') {
      pushValue(name, value, node.category ?? categoryHint, node);
    }

    const entries = Object.entries(node);
    entries.forEach(([key, val]) => {
      if (val === null || typeof val !== 'object') return;
      const lowered = normalizeKey(key);
      const nextCategory =
        node.category ??
        (lowered.includes('openness')
          ? 'openness'
          : lowered.includes('self-transc')
            ? 'self-transcendence'
            : lowered.includes('self-enh')
              ? 'self-enhancement'
              : lowered.includes('conserv')
                ? 'conservation'
                : categoryHint);
      visit(val, nextCategory);
    });
  };

  visit(section?.data ?? section);

  return Array.from(seen.values());
};

const deriveInsightEntriesFromSection = (
  section: any,
  existing?: InsightIndexEntry[]
): InsightIndexEntry[] | null => {
  const arr = coerceArray(section?.data ?? section);
  if (!arr.length) return existing ?? null;

  const result: InsightIndexEntry[] = arr
    .map((item: any) => {
      const insight = item?.insight || item?.label || item?.name;
      const index = extractNumber(item?.index ?? item?.value ?? item?.score ?? item);
      if (!insight || typeof index !== 'number') return null;
      const composition = extractNumber(
        item?.composition ??
          item?.compositionPercent ??
          item?.percent ??
          item?.percentage ??
          item?.share
      );
      return {
        insight,
        index,
        composition: typeof composition === 'number' ? composition : undefined,
      };
    })
    .filter(Boolean) as InsightIndexEntry[];

  return result.length ? result : existing ?? null;
};

const parseBrandList = (input: any): BrandAffinityData['retailers'] => {
  const arr = coerceArray(input);
  return arr
    .map((item: any) => {
      const name = item?.name || item?.label || item?.brand || item?.value || item?.title;
      const index = extractNumber(item?.index ?? item?.value ?? item?.score ?? item);
      if (!name || typeof index !== 'number') return null;
      const composition = extractNumber(
        item?.composition ?? item?.compositionPercent ?? item?.percent ?? item?.percentage
      );
      return {
        name,
        index,
        composition: typeof composition === 'number' ? composition : 0,
      };
    })
    .filter(Boolean) as BrandAffinityData['retailers'];
};

const deriveBrandAffinityFromSection = (
  section: any,
  existing?: BrandAffinityData | null
): BrandAffinityData | null => {
  const root = section?.data ?? section;
  if (!root || typeof root !== 'object') return existing ?? null;

  const result: BrandAffinityData =
    existing !== null && existing !== undefined
      ? {
          retailers: existing.retailers ? [...existing.retailers] : [],
          restaurants: existing.restaurants ? [...existing.restaurants] : [],
          hotels: existing.hotels ? [...existing.hotels] : [],
          airlines: existing.airlines ? [...existing.airlines] : [],
          autoBrands: existing.autoBrands ? [...existing.autoBrands] : [],
        }
      : {
          retailers: [],
          restaurants: [],
          hotels: [],
          airlines: [],
          autoBrands: [],
        };

  let updated = false;
  const sectionEntries = Array.isArray(root)
    ? root
    : Object.entries(root).map(([key, value]) => ({ title: key, data: value }));

  const applyIfPresent = (
    predicate: (title: string) => boolean,
    assign: (list: BrandAffinityData['retailers']) => void
  ) => {
    const found = sectionEntries.find((entry: any) => predicate(String(entry.title || '')));
    if (found) {
      const parsed = parseBrandList(found.data);
      if (parsed.length) {
        assign(parsed);
        updated = true;
      }
    }
  };

  applyIfPresent(
    (title) => normalizeKey(title).includes('retail'),
    (list) => {
      result.retailers = list;
    }
  );
  applyIfPresent(
    (title) => normalizeKey(title).includes('restaurant') || normalizeKey(title).includes('food'),
    (list) => {
      result.restaurants = list;
    }
  );
  applyIfPresent(
    (title) => normalizeKey(title).includes('hotel'),
    (list) => {
      result.hotels = list;
    }
  );
  applyIfPresent(
    (title) =>
      normalizeKey(title).includes('airline') ||
      normalizeKey(title).includes('air ') ||
      normalizeKey(title).includes('airtravel'),
    (list) => {
      result.airlines = list;
    }
  );
  applyIfPresent(
    (title) =>
      normalizeKey(title).includes('auto') ||
      normalizeKey(title).includes('car') ||
      normalizeKey(title).includes('vehicle'),
    (list) => {
      result.autoBrands = list;
    }
  );

  return updated ? result : existing ?? null;
};

const normalizeDemographicsPayload = (
  payload: any,
  existing?: DemographicsData | null
): DemographicsData | null => {
  if (!payload) return existing ?? null;

  const canonicalCandidate = payload?.data ?? payload;
  if (looksLikeCanonicalDemographics(canonicalCandidate)) {
    const sectionLike = {
      data: canonicalCandidate,
      metrics: payload?.metrics ?? payload?.meta ?? {},
    };
    return deriveDemographicsFromAudienceSection(sectionLike, existing);
  }

  const sectionLike =
    typeof payload === 'object'
      ? {
          data: payload.data ?? payload,
          metrics: payload.metrics ?? payload.meta ?? {},
        }
      : null;
  return sectionLike
    ? deriveDemographicsFromAudienceSection(sectionLike, existing)
    : existing ?? null;
};

const looksLikeCanonicalDemographics = (value: any): boolean => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return Boolean(
    candidate.gender ||
    candidate.age ||
    candidate.ageGroups ||
    candidate.education ||
    candidate.employment ||
    candidate.income ||
    candidate.marital ||
    candidate.maritalStatus ||
    candidate.children
  );
};

const normalizePersonalValuesPayload = (
  payload: any,
  existing?: PersonalValue[]
): PersonalValue[] | null => {
  if (!payload) return existing ?? null;
  const root =
    payload?.personalValues ??
    payload?.personal_values ??
    payload?.values ??
    payload?.data ??
    payload;
  return derivePersonalValuesFromSection({ data: root }, existing);
};

const normalizeInsightPayload = (
  payload: any,
  existing?: InsightIndexEntry[]
): InsightIndexEntry[] | null => {
  if (!payload) return existing ?? null;
  return deriveInsightEntriesFromSection({ data: payload }, existing);
};

const normalizeBrandAffinityPayload = (
  payload: any,
  existing?: BrandAffinityData | null
): BrandAffinityData | null => {
  if (!payload) return existing ?? null;
  return deriveBrandAffinityFromSection({ data: payload }, existing);
};

const createInitialPageContent = (content?: PageContent): PageContent => {
  const ensureTopicArray = (topics?: ContentTopic[]) => {
    if (!topics || topics.length === 0) {
      return [{ id: `topic-${Date.now()}`, title: '', description: '' }];
    }
    return topics.map((topic, idx) => ({
      id: topic.id || `topic-${idx}-${Date.now()}`,
      title: topic.title || '',
      description: topic.description || '',
      bullets: topic.bullets ? [...topic.bullets] : [],
    }));
  };

  const ensureSupportingPoints = (points?: HighlightContent['supportingPoints']) => {
    if (!points || points.length === 0) return [];
    return points.map((point) => ({
      label: point.label || '',
      value: point.value || '',
    }));
  };

  return {
    landing: {
      hero: { ...(content?.landing?.hero || {}) },
      keyInsight: {
        ...(content?.landing?.keyInsight || {}),
        supportingPoints: ensureSupportingPoints(content?.landing?.keyInsight?.supportingPoints),
      },
      seoSummary: { ...(content?.landing?.seoSummary || {}) },
      researchSummary: { ...(content?.landing?.researchSummary || {}) },
    },
    seo: {
      hero: { ...(content?.seo?.hero || {}) },
      intro: { ...(content?.seo?.intro || {}) },
      contentTopics: ensureTopicArray(content?.seo?.contentTopics),
      notes: content?.seo?.notes || '',
    },
    research: {
      hero: { ...(content?.research?.hero || {}) },
      intro: { ...(content?.research?.intro || {}) },
    },
    generalInfo: {
      topTitle: content?.generalInfo?.topTitle || '',
      topTitleSubhead: content?.generalInfo?.topTitleSubhead || '',
      headline: content?.generalInfo?.headline || '',
      subhead: content?.generalInfo?.subhead || '',
      mainContent: toRichTextHtml(content?.generalInfo?.mainContent || ''),
    },
  };
};

interface AdminPanelProps {
  clientData: ClientData;
  onSave: (data: ClientData) => Promise<ClientData> | ClientData;
  onClose: () => void;
}

export default function AdminPanel({ clientData, onSave, onClose }: AdminPanelProps) {
  const defaultUiSettings = () => ({
    nav: {
      overview: true,
      seo: true,
      research: true,
      generalInfo: false,
    },
    landingCards: {
      seoSummary: true,
      researchSummary: true
    },
    legacyCharts: {
      demographics: true,
      personalValues: true,
      psychologicalDrivers: true,
      hobbiesAndHabits: true,
      dailyRoutine: true,
      brandAffinity: true
    },
    sections: {} as Record<string, boolean>,
    theme: { primary: '#3E72B5', secondary: '#FDBA19', background: '#99999' },
    personalValues: { topWriteUps: ['', '', ''] }
  });

  const [formData, setFormData] = useState({
    name: clientData.name,
    slug: clientData.slug,
    headline: clientData.headline,
    introduction: clientData.introduction,
    logo: clientData.logo || ''
  });
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [uploadHint, setUploadHint] = useState<string | undefined>(undefined);
  const [currentClientData, setCurrentClientData] = useState(clientData);
  const [demographicsForm, setDemographicsForm] = useState<DemographicsFormState>(
    createDemographicsFormState(clientData.demographics)
  );
  const [personalValuesForm, setPersonalValuesForm] = useState<PersonalValuesFormState>(
    createPersonalValuesFormState(clientData.personalValues)
  );
  const [isSaving, setIsSaving] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [lastOpenAIOutput, setLastOpenAIOutput] = useState<any>(null);
  const [uiSettings, setUiSettings] = useState(currentClientData.uiSettings || defaultUiSettings());
  const [pageContent, setPageContent] = useState<PageContent>(createInitialPageContent(currentClientData.pageContent));
  const [aeoGeoSeoText, setAeoGeoSeoText] = useState<string>(
    (() => {
      const sec = (currentClientData.flexibleSections || []).find((s: any) => (s?.title || '').toLowerCase().includes('aeo/geo/seo')) as any;
      return (sec && (sec.data?.opportunitiesText || sec.data?.opportunities)) || '';
    })()
  );
  const [aeoRows, setAeoRows] = useState<Array<{ name: string; sov: string; sentiment: string; mentions: string }>>(() => {
    const sec = (currentClientData.flexibleSections || []).find((s: any) => (s?.title || '').toLowerCase().includes('aeo/geo/seo')) as any;
    const pts = Array.isArray(sec?.data?.points) ? sec.data.points : [];
    const base = (pts as any[]).map(p => ({
      name: String(p.name ?? ''),
      sov: String(p.shareOfVoice ?? ''),
      sentiment: String(p.sentiment ?? ''),
      mentions: String(p.size ?? p.mentions ?? '')
    }));
    while (base.length < 5) base.push({ name: '', sov: '', sentiment: '', mentions: '' });
    return base.slice(0, 5);
  });
  const [pagePasswordInput, setPagePasswordInput] = useState('');
  const [clearPagePassword, setClearPagePassword] = useState(false);

  // Audience management state
  const [audiences, setAudiences] = useState<Audience[]>(clientData.audiences || []);
  const [selectedAudienceId, setSelectedAudienceId] = useState<string | undefined>(clientData.currentAudienceId);
  const [newAudienceName, setNewAudienceName] = useState('');
  const [isCreatingAudience, setIsCreatingAudience] = useState(false);
  const [audienceError, setAudienceError] = useState<string | null>(null);

  // Load audiences on mount
  useEffect(() => {
    if (clientData.id) {
      ClientService.getClientAudiences(clientData.id).then(setAudiences).catch(console.error);
    }
  }, [clientData.id]);

  const handleCreateAudience = async () => {
    if (!newAudienceName.trim() || !currentClientData.id) return;
    setIsCreatingAudience(true);
    setAudienceError(null);
    try {
      const newAudience = await ClientService.createAudience(currentClientData.id, newAudienceName.trim());
      setAudiences((prev) => [...prev, newAudience]);
      setSelectedAudienceId(newAudience.id);
      setNewAudienceName('');
    } catch (error: any) {
      setAudienceError(error.message || 'Failed to create audience');
    } finally {
      setIsCreatingAudience(false);
    }
  };

  const handleDeleteAudience = async (audienceId: string) => {
    if (!confirm('Delete this audience and all its data? This cannot be undone.')) return;
    try {
      await ClientService.deleteAudience(audienceId);
      setAudiences((prev) => prev.filter((a) => a.id !== audienceId));
      if (selectedAudienceId === audienceId) {
        const remaining = audiences.filter((a) => a.id !== audienceId);
        setSelectedAudienceId(remaining[0]?.id);
      }
    } catch (error: any) {
      setAudienceError(error.message || 'Failed to delete audience');
    }
  };

  const hashPassword = async (password: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hashBuffer))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  };

  useEffect(() => {
    setPageContent(createInitialPageContent(currentClientData.pageContent));
  }, [currentClientData.pageContent]);

  useEffect(() => {
    setPersonalValuesForm(createPersonalValuesFormState(currentClientData.personalValues));
  }, [currentClientData.personalValues]);

  useEffect(() => {
    setCurrentClientData(clientData);
    setFormData({
      name: clientData.name,
      slug: clientData.slug,
      headline: clientData.headline,
      introduction: clientData.introduction,
      logo: clientData.logo || ''
    });
    setUiSettings(clientData.uiSettings || defaultUiSettings());
    setPageContent(createInitialPageContent(clientData.pageContent));
    setDemographicsForm(createDemographicsFormState(clientData.demographics));
    const seoSection = (clientData.flexibleSections || []).find((s: any) => (s?.title || '').toLowerCase().includes('aeo/geo/seo')) as any;
    setAeoGeoSeoText((seoSection && (seoSection.data?.opportunitiesText || seoSection.data?.opportunities)) || '');
    const pts = Array.isArray(seoSection?.data?.points) ? seoSection.data.points : [];
    const base = (pts as any[]).map(p => ({
      name: String(p.name ?? ''),
      sov: String(p.shareOfVoice ?? p.sov ?? ''),
      sentiment: String(p.sentiment ?? ''),
      mentions: String(p.size ?? p.mentions ?? '')
    }));
    while (base.length < 5) base.push({ name: '', sov: '', sentiment: '', mentions: '' });
    setAeoRows(base.slice(0, 5));
    setPagePasswordInput('');
    setClearPagePassword(false);
  }, [clientData]);

  useEffect(() => {
    setDemographicsForm(createDemographicsFormState(currentClientData.demographics));
  }, [currentClientData.demographics]);

  const updateLandingHero = (field: keyof HeroContent, value: string) => {
    setPageContent(prev => ({
      ...prev,
      landing: {
        ...(prev.landing || {}),
        hero: {
          ...(prev.landing?.hero || {}),
          [field]: value,
        },
      },
    }));
  };

  const updateLandingKeyInsight = (field: keyof HighlightContent, value: string) => {
    setPageContent(prev => ({
      ...prev,
      landing: {
        ...(prev.landing || {}),
        keyInsight: {
          ...(prev.landing?.keyInsight || {}),
          [field]: value,
        },
      },
    }));
  };

  const updateLandingKeyPoint = (index: number, key: 'label' | 'value', value: string) => {
    setPageContent(prev => {
      const points = prev.landing?.keyInsight?.supportingPoints
        ? [...prev.landing.keyInsight.supportingPoints]
        : [];
      points[index] = {
        ...(points[index] || { label: '', value: '' }),
        [key]: value,
      };
      return {
        ...prev,
        landing: {
          ...(prev.landing || {}),
          keyInsight: {
            ...(prev.landing?.keyInsight || {}),
            supportingPoints: points,
          },
        },
      };
    });
  };

const handleGenderChange = (field: keyof DemographicsFormState['gender'], value: string) => {
  setDemographicsForm((prev) => ({
    ...prev,
    gender: { ...prev.gender, [field]: value },
  }));
};

const handleAgeGroupChange = (
  field: keyof DemographicsData['ageGroups'],
  value: string
) => {
    setDemographicsForm((prev) => ({
      ...prev,
      ageGroups: { ...prev.ageGroups, [field]: value },
    }));
  };

  const handleEducationChange = (
    field: keyof DemographicsData['education'],
    value: string
  ) => {
    setDemographicsForm((prev) => ({
      ...prev,
      education: { ...prev.education, [field]: value },
    }));
  };

const handleEmploymentChange = (
  field: keyof DemographicsFormState['employment'],
  value: string
) => {
  setDemographicsForm((prev) => ({
    ...prev,
    employment: { ...prev.employment, [field]: value },
  }));
};

const handleTargetableChange = (value: string) => {
  setDemographicsForm((prev) => ({
    ...prev,
    targetableIds: value,
  }));
};

  const addLandingKeyPoint = () => {
    setPageContent(prev => {
      const points = prev.landing?.keyInsight?.supportingPoints
        ? [...prev.landing.keyInsight.supportingPoints]
        : [];
      points.push({ label: '', value: '' });
      return {
        ...prev,
        landing: {
          ...(prev.landing || {}),
          keyInsight: {
            ...(prev.landing?.keyInsight || {}),
            supportingPoints: points,
          },
        },
      };
    });
  };

  const removeLandingKeyPoint = (index: number) => {
    setPageContent(prev => {
      const points = prev.landing?.keyInsight?.supportingPoints
        ? [...prev.landing.keyInsight.supportingPoints]
        : [];
      points.splice(index, 1);
      return {
        ...prev,
        landing: {
          ...(prev.landing || {}),
          keyInsight: {
            ...(prev.landing?.keyInsight || {}),
            supportingPoints: points,
          },
        },
      };
    });
  };

  const updateLandingCard = (card: 'seoSummary' | 'researchSummary', field: keyof InsightCardContent, value: string) => {
    setPageContent(prev => ({
      ...prev,
      landing: {
        ...(prev.landing || {}),
        [card]: {
          ...(prev.landing?.[card] || {}),
          [field]: value,
        },
      },
    }));
  };

  const updateSeoHero = (field: keyof HeroContent, value: string) => {
    setPageContent(prev => ({
      ...prev,
      seo: {
        ...(prev.seo || {}),
        hero: {
          ...(prev.seo?.hero || {}),
          [field]: value,
        },
      },
    }));
  };

  const updateSeoIntro = (field: keyof InsightCardContent, value: string) => {
    setPageContent(prev => ({
      ...prev,
      seo: {
        ...(prev.seo || {}),
        intro: {
          ...(prev.seo?.intro || {}),
          [field]: value,
        },
      },
    }));
  };

  const updateContentTopic = (index: number, field: keyof ContentTopic, value: string) => {
    setPageContent(prev => {
      const topics = prev.seo?.contentTopics ? [...prev.seo.contentTopics] : [];
      topics[index] = {
        ...(topics[index] || { id: `topic-${index}-${Date.now()}`, title: '' }),
        [field]: value,
      } as ContentTopic;
      return {
        ...prev,
        seo: {
          ...(prev.seo || {}),
          contentTopics: topics,
        },
      };
    });
  };

  const updateContentTopicBullet = (topicIndex: number, bulletIndex: number, value: string) => {
    setPageContent(prev => {
      const topics = prev.seo?.contentTopics ? [...prev.seo.contentTopics] : [];
      const topic = topics[topicIndex] || { title: '', bullets: [] };
      const bullets = topic.bullets ? [...topic.bullets] : [];
      bullets[bulletIndex] = value;
      topics[topicIndex] = { ...topic, bullets };
      return {
        ...prev,
        seo: {
          ...(prev.seo || {}),
          contentTopics: topics,
        },
      };
    });
  };

  const addContentTopicBullet = (topicIndex: number) => {
    setPageContent(prev => {
      const topics = prev.seo?.contentTopics ? [...prev.seo.contentTopics] : [];
      const topic = topics[topicIndex] || { title: '', bullets: [] };
      const bullets = topic.bullets ? [...topic.bullets] : [];
      bullets.push('');
      topics[topicIndex] = { ...topic, bullets };
      return {
        ...prev,
        seo: {
          ...(prev.seo || {}),
          contentTopics: topics,
        },
      };
    });
  };

  const removeContentTopicBullet = (topicIndex: number, bulletIndex: number) => {
    setPageContent(prev => {
      const topics = prev.seo?.contentTopics ? [...prev.seo.contentTopics] : [];
      const topic = topics[topicIndex] || { title: '', bullets: [] };
      const bullets = topic.bullets ? [...topic.bullets] : [];
      bullets.splice(bulletIndex, 1);
      topics[topicIndex] = { ...topic, bullets };
      return {
        ...prev,
        seo: {
          ...(prev.seo || {}),
          contentTopics: topics,
        },
      };
    });
  };

  const addContentTopic = () => {
    setPageContent(prev => ({
      ...prev,
      seo: {
        ...(prev.seo || {}),
        contentTopics: [
          ...(prev.seo?.contentTopics || []),
          { id: `topic-${Date.now()}`, title: '', description: '', bullets: [] },
        ],
      },
    }));
  };

  const removeContentTopic = (index: number) => {
    setPageContent(prev => {
      const topics = prev.seo?.contentTopics ? [...prev.seo.contentTopics] : [];
      topics.splice(index, 1);
      return {
        ...prev,
        seo: {
          ...(prev.seo || {}),
          contentTopics: topics.length > 0 ? topics : [{ id: `topic-${Date.now()}`, title: '', description: '' }],
        },
      };
    });
  };

  const updateSeoNotes = (value: string) => {
    setPageContent(prev => ({
      ...prev,
      seo: {
        ...(prev.seo || {}),
        notes: value,
      },
    }));
  };

  const updateResearchHero = (field: keyof HeroContent, value: string) => {
    setPageContent(prev => ({
      ...prev,
      research: {
        ...(prev.research || {}),
        hero: {
          ...(prev.research?.hero || {}),
          [field]: value,
        },
      },
    }));
  };

  const updateResearchIntro = (field: keyof InsightCardContent, value: string) => {
    setPageContent(prev => ({
      ...prev,
      research: {
        ...(prev.research || {}),
        intro: {
          ...(prev.research?.intro || {}),
          [field]: value,
        },
      },
    }));
  };

  const updateGeneralInfo = (
    field: 'topTitle' | 'topTitleSubhead' | 'headline' | 'subhead' | 'mainContent',
    value: string
  ) => {
    setPageContent((prev) => ({
      ...prev,
      generalInfo: {
        ...(prev.generalInfo || {}),
        [field]: value,
      },
    }));
  };

  const handleImageUpload = async (file: File, onUrl: (url: string) => void) => {
    if (!currentClientData.id) {
      alert('Please save the client before uploading images.');
      return;
    }
    try {
      const ext = file.name.split('.').pop();
      const filePath = `${currentClientData.id}/page-content/${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('client-files')
        .upload(filePath, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data } = supabase.storage.from('client-files').getPublicUrl(filePath);
      onUrl(data.publicUrl);
    } catch (err: any) {
      console.error('Image upload failed:', err);
      alert(err?.message || 'Failed to upload image');
    }
  };

  const handleRichTextAssetUpload = async (
    file: File,
    kind: 'image' | 'video'
  ): Promise<string> => {
    if (!currentClientData.id) {
      throw new Error('Please save the client before uploading assets.');
    }
    const ext = file.name.split('.').pop() || (kind === 'image' ? 'png' : 'mp4');
    const baseName = file.name.replace(/\.[^.]+$/, '');
    const safeName = baseName.replace(/[^a-zA-Z0-9._-]/g, '-');
    const filePath = `${currentClientData.id}/page-content/rich-text/${Date.now()}-${safeName}.${ext}`;
    const { error: uploadError } = await supabase.storage
      .from('client-files')
      .upload(filePath, file, { upsert: true });
    if (uploadError) throw uploadError;
    const { data } = supabase.storage.from('client-files').getPublicUrl(filePath);
    return data.publicUrl;
  };

  const renderImageInput = (
    value: string | undefined,
    onChange: (val: string) => void,
    placeholder: string
  ) => (
    <div className="space-y-3">
      <div className="relative border-2 border-dashed border-gray-300 rounded-lg h-40 flex items-center justify-center bg-white overflow-hidden">
        <input
          type="file"
          accept="image/*"
          className="absolute inset-0 opacity-0 cursor-pointer"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) {
              handleImageUpload(file, onChange);
            }
          }}
        />
        {value ? (
          <div className="absolute inset-0">
            <img src={value} alt="Background preview" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/30" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-white text-sm font-semibold">Click to replace image</span>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
              }}
              className="absolute top-3 right-3 bg-white/90 text-xs font-semibold text-red-600 px-2 py-1 rounded"
            >
              Remove
            </button>
          </div>
        ) : (
          <div className="text-center px-4">
            <div className="text-sm font-semibold text-gray-700">Click to upload image</div>
            <div className="text-xs text-gray-500 mt-1">JPG, PNG, or GIF (max 10MB)</div>
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 min-w-[220px] px-3 py-2 border rounded"
          placeholder={`${placeholder} or paste public URL`}
        />
        {value && (
          <button
            type="button"
            onClick={() => onChange('')}
            className="text-xs text-red-500 hover:text-red-600"
          >
            Clear URL
          </button>
        )}
      </div>
    </div>
  );

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const logoUrl = event.target?.result as string;
        setFormData({ ...formData, logo: logoUrl });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      let pagePasswordHash = currentClientData.pagePasswordHash || null;
      if (clearPagePassword) {
        pagePasswordHash = null;
      } else if (pagePasswordInput.trim()) {
        pagePasswordHash = await hashPassword(pagePasswordInput.trim());
      }

      const updatedClient: ClientData = {
        ...currentClientData,
        ...formData,
        slug: formData.slug.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
        uiSettings: uiSettings,
        pageContent: pageContent,
        pagePasswordHash,
        personalValues: PERSONAL_VALUE_FIELDS.map((field) => ({
          name: field.label,
          value: parseNumericInput(personalValuesForm[field.label] || ''),
          category: field.category,
        })),
        updatedAt: new Date().toISOString()
      };

      console.log('Saving client data:', updatedClient);
      const savedClient = await onSave(updatedClient);
      const nextClient = savedClient || updatedClient;

      // Save demographics if we have a client ID
      if (currentClientData.id) {
        const demographicsPayload = {
          targetableIds: demographicsForm.targetableIds?.trim() || '0',
          gender: {
            male: parseNumericInput(demographicsForm.gender.male),
            female: parseNumericInput(demographicsForm.gender.female),
          },
          ageGroups: DEMO_AGE_KEYS.reduce((acc, key) => {
            acc[key] = parseNumericInput(demographicsForm.ageGroups[key]);
            return acc;
          }, {} as Record<keyof DemographicsData['ageGroups'], number>),
          education: DEMO_EDUCATION_KEYS.reduce((acc, item) => {
            acc[item.key] = parseNumericInput(demographicsForm.education[item.key]);
            return acc;
          }, {} as Record<keyof DemographicsData['education'], number>),
          employment: {
            fullTime: parseNumericInput(demographicsForm.employment.fullTime),
            selfEmployed: parseNumericInput(demographicsForm.employment.selfEmployed),
            partTime: parseNumericInput(demographicsForm.employment.partTime),
            unemployed: parseNumericInput(demographicsForm.employment.unemployed),
            homemaker: parseNumericInput(demographicsForm.employment.homemaker),
            student: parseNumericInput(demographicsForm.employment.student),
            retired: parseNumericInput(demographicsForm.employment.retired),
            index: parseNumericInput(demographicsForm.employment.index),
            compositionPrimary: parseNumericInput(demographicsForm.employment.compositionPrimary),
          },
        };
        await ClientService.updateClientData(currentClientData.id, 'demographics', demographicsPayload);
        console.log('Demographics saved:', demographicsPayload);
      }

      setCurrentClientData(prev => ({ ...prev, ...nextClient }));
      setFormData({
        name: nextClient.name,
        slug: nextClient.slug,
        headline: nextClient.headline,
        introduction: nextClient.introduction,
        logo: nextClient.logo || ''
      });
      setUiSettings(nextClient.uiSettings || defaultUiSettings());
      setPageContent(createInitialPageContent(nextClient.pageContent));
      setPagePasswordInput('');
      setClearPagePassword(false);
      alert('All changes saved.');
    } catch (error) {
      console.error('Error saving client:', error);
      alert('Failed to save client. Please check the console for details.');
    } finally {
      setIsSaving(false);
    }
  };

  const ensureClientAndOpen = async (hint?: string) => {
    // Ensure we have a real client id before allowing uploads to avoid cross-brand mixups
    if (!currentClientData.id) {
      try {
        const normalizedSlug = formData.slug
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '');
        const created = await ClientService.createClient({
          name: formData.name,
          slug: normalizedSlug,
          logo: formData.logo,
          headline: formData.headline,
          introduction: formData.introduction,
        });
        setCurrentClientData(prev => ({
          ...prev,
          id: created.id,
          name: created.name,
          slug: created.slug,
          logo: created.logo_url,
          headline: created.headline,
          introduction: created.introduction,
        } as any));
        setUploadHint(hint);
        setShowFileUpload(true);
      } catch (err: any) {
        console.error('Failed to create client before upload:', err);
        alert(`Please save a unique client first (slug may be taken). Error: ${err?.message || 'Unknown error'}`);
      }
    } else {
      setUploadHint(hint);
      setShowFileUpload(true);
    }
  };

  const handleDataExtracted = async (data: any, dataType: string) => {
    try {
      // Store the raw OpenAI output for debugging
      setLastOpenAIOutput({ dataType, data, timestamp: new Date().toISOString() });
      
      console.log('🤖 OpenAI Output Received:', { dataType, data });
      
      // Update the current client data with extracted data
      const updatedData = { ...currentClientData };
      let derivedDemographics: DemographicsData | null = null;
      const derivedUpdates: {
        personalValues?: PersonalValue[];
        psychologicalDrivers?: InsightIndexEntry[];
        hobbiesAndHabits?: InsightIndexEntry[];
        dailyRoutine?: InsightIndexEntry[];
        brandAffinity?: BrandAffinityData | null;
      } = {};
      const pendingDerivedSaves: Array<{ type: string; payload: any }> = [];
      let primaryPayload: any = data;
      
      if (dataType === 'flexible_sections') {
        const normalizeTitle = (t: string) => {
          const s = (t || '').toLowerCase();
          if (s.includes('demographic highlights')) return 'Audience';
          if (s.includes('audience')) return 'Audience';
          if (s.includes('education')) return 'Education';
          if (s.includes('employment')) return 'Employment Status';
          if (s.includes('brand affinity')) return 'Brand Affinity';
          if (s.includes('site affinity') || s.includes('publisher')) return 'Site Affinity';
          if (s.includes('category affinity')) return 'Category Affinity';
          return t || 'Section';
        };

        const incoming = Array.isArray(data) ? data : [];
        const existing = Array.isArray(updatedData.flexibleSections) ? updatedData.flexibleSections : [];

        // Build map of existing by normalized title
        const map = new Map<string, any>();
        existing.forEach((s: any) => {
          const key = normalizeTitle(s.title).toLowerCase();
          map.set(key, { ...s });
        });

        // Remove existing audience entries so new uploads replace them
        map.delete('audience');
        map.delete('demographic highlights');

        incoming.forEach((section: any) => {
          const canonicalTitle = normalizeTitle(section.title);
          const key = canonicalTitle.toLowerCase();
          if (key === 'audience') {
            derivedDemographics = deriveDemographicsFromAudienceSection(
              section,
              updatedData.demographics as DemographicsData | null
            );
            return;
          }
          if (key === 'personal values') {
            const derived = derivePersonalValuesFromSection(section);
            if (derived) derivedUpdates.personalValues = derived;
          } else if (key === 'psychological drivers') {
            const derived = deriveInsightEntriesFromSection(
              section,
              updatedData.psychologicalDrivers
            );
            if (derived) derivedUpdates.psychologicalDrivers = derived;
          } else if (key === 'hobbies & habits' || key === 'hobbies and habits') {
            const derived = deriveInsightEntriesFromSection(section) ?? [];
            derivedUpdates.hobbiesAndHabits = derived;
          } else if (key === 'daily routine') {
            const derived = deriveInsightEntriesFromSection(section, updatedData.dailyRoutine);
            if (derived) derivedUpdates.dailyRoutine = derived;
          } else if (key === 'brand affinity') {
            const derived = deriveBrandAffinityFromSection(
              section,
              updatedData.brandAffinity as BrandAffinityData | null
            );
            if (derived) derivedUpdates.brandAffinity = derived;
          }
          map.set(key, { ...section, title: section.title || canonicalTitle });
        });

        updatedData.flexibleSections = Array.from(map.values());
        console.log('📊 Merged flexible sections:', updatedData.flexibleSections);

        if (derivedDemographics) {
          updatedData.demographics = derivedDemographics;
          console.log('🧭 Derived demographics from Audience section:', derivedDemographics);
        }
        // Update local state with derived data for immediate UI display
        // NOTE: We do NOT save derived data to DB here because FileUpload already
        // saves each dataset type independently. Saving here causes race conditions
        // where derived (potentially empty) data overwrites direct extractions.
        if (derivedUpdates.personalValues) {
          updatedData.personalValues = derivedUpdates.personalValues;
          // Don't add to pendingDerivedSaves - FileUpload handles personal_values save
        }
        if (derivedUpdates.psychologicalDrivers) {
          updatedData.psychologicalDrivers = derivedUpdates.psychologicalDrivers;
          // Don't add to pendingDerivedSaves - FileUpload handles this save
        }
        if (derivedUpdates.hobbiesAndHabits) {
          updatedData.hobbiesAndHabits = derivedUpdates.hobbiesAndHabits;
          // Don't add to pendingDerivedSaves - FileUpload handles this save
        }
        if (derivedUpdates.dailyRoutine) {
          updatedData.dailyRoutine = derivedUpdates.dailyRoutine;
          // Don't add to pendingDerivedSaves - FileUpload handles this save
        }
        if (derivedUpdates.brandAffinity) {
          updatedData.brandAffinity = derivedUpdates.brandAffinity;
          // Don't add to pendingDerivedSaves - FileUpload handles this save
        }
        if (derivedDemographics) {
          // Demographics is special - only save if not handled by FileUpload
          // pendingDerivedSaves.push({ type: 'demographics', payload: derivedDemographics });
        }
      } else if (dataType === 'insights') {
        updatedData.insights = data;
        console.log('💡 Setting insights:', data);
      } else if (dataType === 'demographics') {
        const normalized = normalizeDemographicsPayload(
          data,
          updatedData.demographics as DemographicsData | null
        );
        if (normalized) {
          updatedData.demographics = normalized;
          primaryPayload = normalized;
        } else {
          updatedData.demographics = data;
        }
      } else if (dataType === 'personal_values') {
        const normalized = normalizePersonalValuesPayload(data) ?? [];
        if (normalized.length > 0) {
          updatedData.personalValues = normalized;
          primaryPayload = normalized;
        }
        pendingDerivedSaves.push({ type: 'personal_values', payload: normalized });
      } else if (dataType === 'psychological_drivers') {
        const normalized = normalizeInsightPayload(data, updatedData.psychologicalDrivers) ?? [];
        updatedData.psychologicalDrivers = normalized;
        primaryPayload = normalized;
        pendingDerivedSaves.push({ type: 'psychological_drivers', payload: normalized });
      } else if (dataType === 'hobbies_habits') {
        const normalized = normalizeInsightPayload(data) ?? [];
        updatedData.hobbiesAndHabits = normalized;
        primaryPayload = normalized;
        pendingDerivedSaves.push({ type: 'hobbies_habits', payload: normalized });
      } else if (dataType === 'daily_routine') {
        const normalized = normalizeInsightPayload(data, updatedData.dailyRoutine) ?? [];
        updatedData.dailyRoutine = normalized;
        primaryPayload = normalized;
        pendingDerivedSaves.push({ type: 'daily_routine', payload: normalized });
      } else if (dataType === 'brand_affinity') {
        const normalized = normalizeBrandAffinityPayload(
          data,
          updatedData.brandAffinity as BrandAffinityData | null
        );
        updatedData.brandAffinity = normalized ?? data;
        primaryPayload = normalized ?? data;
        if (normalized) {
          pendingDerivedSaves.push({ type: 'brand_affinity', payload: normalized });
        }
      } else if (dataType === 'custom_charts') {
        updatedData.customCharts = Array.isArray(data) ? data : [];
        primaryPayload = updatedData.customCharts;
        pendingDerivedSaves.push({ type: 'custom_charts', payload: updatedData.customCharts });
      }

      setCurrentClientData(updatedData);
      console.log('🔄 Updated client data:', updatedData);

      // If we have a client ID, save to database (persist merged data for flexible_sections)
      // Use currentClientData.id (local state) instead of clientData.id (prop) to handle newly created clients
      if (currentClientData.id) {
        const payload =
          dataType === 'flexible_sections'
            ? (updatedData.flexibleSections || [])
            : dataType === 'insights'
              ? (updatedData.insights || [])
              : primaryPayload;
        console.log('💾 Saving to database...', {
          clientId: currentClientData.id,
          dataType,
          saved: payload,
        });
        await ClientService.updateClientData(currentClientData.id, dataType, payload);
        console.log('✅ Saved to database successfully');

        for (const derived of pendingDerivedSaves) {
          if (derived.type === dataType) continue;
          await ClientService.updateClientData(currentClientData.id, derived.type, derived.payload);
          console.log(`✅ Saved derived ${derived.type} to database`);
        }
      }

      setShowFileUpload(false);
    } catch (error) {
      console.error('❌ Failed to save extracted data:', error);
      alert(`Failed to save data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">Admin Panel</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form id="admin-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Client Logo Section */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Client Branding</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client Logo
                </label>
                <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-teal-400 transition-colors">
                  {formData.logo ? (
                    <div className="space-y-3">
                      <img
                        src={formData.logo}
                        alt="Client Logo"
                        className="max-h-20 mx-auto"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, logo: '' })}
                        className="text-red-600 hover:text-red-800 text-sm relative z-10"
                      >
                        Remove Logo
                      </button>
                    </div>
                  ) : (
                    <div className="relative z-10 pointer-events-none">
                      <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Upload client logo</p>
                    </div>
                  )}
                  {!formData.logo && (
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-0"
                    />
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Client Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Client Slug (URL)
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                      welcome.littlefieldagency.com/
                    </span>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Client Page Password
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="password"
                      value={pagePasswordInput}
                      onChange={(e) => {
                        setPagePasswordInput(e.target.value);
                        if (clearPagePassword) setClearPagePassword(false);
                      }}
                      placeholder={currentClientData.pagePasswordHash ? 'Enter new password to change' : 'Set a password to protect this page'}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                    {currentClientData.pagePasswordHash && (
                      <button
                        type="button"
                        onClick={() => {
                          setClearPagePassword(true);
                          setPagePasswordInput('');
                        }}
                        className="text-xs text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {currentClientData.pagePasswordHash
                      ? clearPagePassword
                        ? 'Password will be removed when you save.'
                        : 'Leave blank to keep the current password or enter a new one to replace it.'
                      : 'Optional: Set a password to require visitors to enter it before viewing this page.'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Audience Management Section (Beta) */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-purple-900">Audience Management</h3>
                <p className="text-xs text-purple-600">Beta feature for /research-beta page - Up to 3 audiences per client</p>
              </div>
              {audiences.length < 3 && (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newAudienceName}
                    onChange={(e) => setNewAudienceName(e.target.value)}
                    placeholder="New audience name..."
                    className="px-3 py-1.5 text-sm border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <button
                    type="button"
                    onClick={handleCreateAudience}
                    disabled={isCreatingAudience || !newAudienceName.trim()}
                    className="flex items-center gap-1 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    Add
                  </button>
                </div>
              )}
            </div>

            {audienceError && (
              <div className="mb-3 p-2 bg-red-100 border border-red-300 rounded text-red-700 text-sm">
                {audienceError}
              </div>
            )}

            {audiences.length === 0 ? (
              <p className="text-sm text-purple-600 italic">No audiences created yet. Create one to start uploading audience-specific data.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {audiences.map((audience) => (
                  <div
                    key={audience.id}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all cursor-pointer ${
                      selectedAudienceId === audience.id
                        ? 'bg-purple-600 text-white border-purple-600 shadow-md'
                        : 'bg-white text-purple-700 border-purple-300 hover:border-purple-400'
                    }`}
                    onClick={() => setSelectedAudienceId(audience.id)}
                  >
                    <span className="font-medium">{audience.name}</span>
                    {audience.isDefault && (
                      <span className={`text-xs px-1.5 py-0.5 rounded ${selectedAudienceId === audience.id ? 'bg-purple-500' : 'bg-purple-100'}`}>
                        Default
                      </span>
                    )}
                    {audiences.length > 1 && !audience.isDefault && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteAudience(audience.id);
                        }}
                        className={`p-0.5 rounded hover:bg-red-100 ${selectedAudienceId === audience.id ? 'text-white hover:text-red-600' : 'text-red-500'}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {selectedAudienceId && (
              <p className="mt-3 text-xs text-purple-600">
                Data uploads below will be saved for: <strong>{audiences.find(a => a.id === selectedAudienceId)?.name || 'Selected Audience'}</strong>
              </p>
            )}
          </div>

          {/* Content Section */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-xl font-bold text-teal-600 mb-4">Research Content</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Headline
                </label>
                <input
                  type="text"
                  value={formData.headline}
                  onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Introduction Paragraph
                </label>
                <textarea
                  value={formData.introduction}
                  onChange={(e) => setFormData({ ...formData, introduction: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                />
              </div>
            </div>
          </div>

          {/* Data Upload Section */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Data Management</h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              <button type="button" onClick={() => ensureClientAndOpen('Psychological Drivers')} className="bg-teal-600 hover:bg-teal-700 text-white px-3 py-2 rounded-lg text-sm">
                Upload Psychological Drivers
              </button>
              <button type="button" onClick={() => ensureClientAndOpen('Hobbies & Habits')} className="bg-teal-600 hover:bg-teal-700 text-white px-3 py-2 rounded-lg text-sm">
                Upload Hobbies &amp; Habits
              </button>
              <button type="button" onClick={() => ensureClientAndOpen('Daily Routine')} className="bg-teal-600 hover:bg-teal-700 text-white px-3 py-2 rounded-lg text-sm">
                Upload Daily Routine
              </button>
              <button type="button" onClick={() => ensureClientAndOpen('Brand Affinity')} className="bg-teal-600 hover:bg-teal-700 text-white px-3 py-2 rounded-lg text-sm">
                Upload Brand Affinity
              </button>
              <button type="button" onClick={() => ensureClientAndOpen('Site Affinity')} className="bg-teal-600 hover:bg-teal-700 text-white px-3 py-2 rounded-lg text-sm">
                Upload Site Affinity
              </button>
            </div>
            
            <div className="text-sm text-gray-600 mb-4 mt-4">
              Upload PDF documents, images, CSV files, or JSON data to automatically extract customer behavior insights using AI.
            </div>
            <div className="text-sm text-gray-600">
              Personal Values is now manual entry below to ensure exact chart values.
            </div>

          </div>

          {/* Demographics manual editor */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900">Demographics (Manual Entry)</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Enter audience percentages manually. These values are saved with the main "Save Changes" button below.
            </p>
            <div className="bg-white border rounded-lg p-4 mb-6">
              <label className="block text-xs font-medium text-gray-600">
                Audience / Targetable IDs
                <input
                  type="text"
                  value={demographicsForm.targetableIds}
                  onChange={(e) => handleTargetableChange(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                  placeholder="e.g. 989K"
                />
              </label>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white border rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Gender (% of audience)</h4>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block text-xs font-medium text-gray-600">
                    Male
                    <input
                      type="number"
                      inputMode="decimal"
                      value={demographicsForm.gender.male}
                      onChange={(e) => handleGenderChange('male', e.target.value)}
                      className="mt-1 w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                      placeholder="e.g. 52"
                    />
                  </label>
                  <label className="block text-xs font-medium text-gray-600">
                    Female
                    <input
                      type="number"
                      inputMode="decimal"
                      value={demographicsForm.gender.female}
                      onChange={(e) => handleGenderChange('female', e.target.value)}
                      className="mt-1 w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                      placeholder="e.g. 48"
                    />
                  </label>
                </div>
              </div>
              <div className="bg-white border rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Employment (% unless noted)</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {DEMO_EMPLOYMENT_FIELDS.map((field) => (
                    <label key={field.key} className="block text-xs font-medium text-gray-600">
                      {field.label}
                      <input
                        type="number"
                        inputMode="decimal"
                        value={demographicsForm.employment[field.key]}
                        onChange={(e) => handleEmploymentChange(field.key, e.target.value)}
                        className="mt-1 w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                        placeholder={field.key === 'index' ? 'e.g. 153' : 'e.g. 25'}
                      />
                    </label>
                  ))}
                </div>
              </div>
              <div className="bg-white border rounded-lg p-4 lg:col-span-2">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Age Groups (% of audience)</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
                  {DEMO_AGE_KEYS.map((key) => (
                    <label key={key} className="block text-xs font-medium text-gray-600">
                      {key}
                      <input
                        type="number"
                        inputMode="decimal"
                        value={demographicsForm.ageGroups[key]}
                        onChange={(e) => handleAgeGroupChange(key, e.target.value)}
                        className="mt-1 w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                        placeholder="e.g. 24"
                      />
                    </label>
                  ))}
                </div>
              </div>
              <div className="bg-white border rounded-lg p-4 lg:col-span-2">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Education (% of audience)</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {DEMO_EDUCATION_KEYS.map((item) => (
                    <label key={item.key} className="block text-xs font-medium text-gray-600">
                      {item.label}
                      <input
                        type="number"
                        inputMode="decimal"
                        value={demographicsForm.education[item.key]}
                        onChange={(e) => handleEducationChange(item.key, e.target.value)}
                        className="mt-1 w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                        placeholder="e.g. 26"
                      />
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Personal Values manual editor */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900">Personal Values (Manual Entry)</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Enter all 19 Personal Values indexes manually. These values are saved with the main "Save Changes" button and power the existing chart unchanged.
            </p>
            <div className="bg-white border rounded-lg p-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {PERSONAL_VALUE_FIELDS.map((field) => (
                  <label key={field.label} className="block text-xs font-medium text-gray-600">
                    {field.label}
                    <input
                      type="number"
                      inputMode="decimal"
                      value={personalValuesForm[field.label] || ''}
                      onChange={(e) =>
                        setPersonalValuesForm((prev) => ({
                          ...prev,
                          [field.label]: e.target.value,
                        }))
                      }
                      className="mt-1 w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                      placeholder="e.g. 164"
                    />
                  </label>
                ))}
              </div>
              <div className="mt-6 border-t border-gray-200 pt-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Top Personal Value Write-Ups</h4>
                <div className="space-y-3">
                  {[0, 1, 2].map((idx) => (
                    <label key={idx} className="block text-xs font-medium text-gray-600">
                      {`Top Personal Value Write-Ups ${idx + 1}`}
                      <textarea
                        rows={3}
                        value={uiSettings.personalValues?.topWriteUps?.[idx] || ''}
                        onChange={(e) =>
                          setUiSettings((prev) => {
                            const next = [
                              prev.personalValues?.topWriteUps?.[0] || '',
                              prev.personalValues?.topWriteUps?.[1] || '',
                              prev.personalValues?.topWriteUps?.[2] || '',
                            ];
                            next[idx] = e.target.value;
                            return {
                              ...prev,
                              personalValues: {
                                ...(prev.personalValues || {}),
                                topWriteUps: next,
                              },
                            };
                          })
                        }
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                        placeholder="Write-up paragraph shown under the corresponding Top 3 value."
                      />
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* AEO/GEO/SEO editor */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-xl font-bold text-teal-600 mb-3">AEO/GEO/SEO Opportunities</h3>
            <p className="text-sm text-gray-600 mb-2">Rich text (HTML allowed). This appears on the right side of the AEO/GEO/SEO section.</p>
            <textarea
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              value={aeoGeoSeoText}
              onChange={(e) => setAeoGeoSeoText(e.target.value)}
            />
            <div className="text-right mt-2">
              <button
                type="button"
                className="px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm"
                onClick={async () => {
                  try {
                    const updated = { ...(currentClientData as any) };
                    const sections = Array.isArray(updated.flexibleSections) ? [...updated.flexibleSections] : [];
                    const idx = sections.findIndex((s: any) => (s?.title || '').toLowerCase().includes('aeo/geo/seo'));
                    if (idx >= 0) {
                      sections[idx] = { ...sections[idx], title: 'AEO/GEO/SEO', data: { ...(sections[idx].data || {}), opportunitiesText: aeoGeoSeoText } };
                    } else {
                      sections.unshift({ title: 'AEO/GEO/SEO', order: -10, data: { opportunitiesText: aeoGeoSeoText } });
                    }
                    updated.flexibleSections = sections;
                    setCurrentClientData(updated);
                    if (currentClientData.id) {
                      await ClientService.updateClientData(currentClientData.id, 'flexible_sections', sections);
                      alert('AEO/GEO/SEO Opportunities saved');
                    }
                  } catch (e) {
                    console.error(e);
                    alert('Failed to save AEO/GEO/SEO text');
                  }
                }}
              >
                Save Opportunities
              </button>
            </div>
          </div>

          {/* AEO/GEO/SEO manual points table */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">AEO/GEO/SEO Manual Points</h3>
            <p className="text-sm text-gray-600 mb-3">Enter up to 5 brands. Columns: Brand, Sentiment (0–100), Share of Voice (%), Total Mentions (controls dot size).</p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-600">
                    <th className="py-2 pr-3">Brand</th>
                    <th className="py-2 pr-3">Sentiment</th>
                    <th className="py-2 pr-3">Share of Voice</th>
                    <th className="py-2 pr-3">Total Mentions</th>
                  </tr>
                </thead>
                <tbody>
                  {aeoRows.map((row, idx) => (
                    <tr key={idx}>
                      <td className="py-1 pr-3">
                        <input value={row.name} onChange={(e)=>{
                          const v = e.target.value; setAeoRows(prev=>prev.map((r,i)=> i===idx?{...r, name:v}:r));
                        }} className="w-full px-2 py-1 border rounded" placeholder="Brand name" />
                      </td>
                      <td className="py-1 pr-3">
                        <input value={row.sentiment} onChange={(e)=>{
                          const v = e.target.value; setAeoRows(prev=>prev.map((r,i)=> i===idx?{...r, sentiment:v}:r));
                        }} className="w-full px-2 py-1 border rounded" placeholder="e.g. 58" />
                      </td>
                      <td className="py-1 pr-3">
                        <input value={row.sov} onChange={(e)=>{
                          const v = e.target.value; setAeoRows(prev=>prev.map((r,i)=> i===idx?{...r, sov:v}:r));
                        }} className="w-full px-2 py-1 border rounded" placeholder="e.g. 22" />
                      </td>
                      <td className="py-1 pr-3">
                        <input value={row.mentions} onChange={(e)=>{
                          const v = e.target.value; setAeoRows(prev=>prev.map((r,i)=> i===idx?{...r, mentions:v}:r));
                        }} className="w-full px-2 py-1 border rounded" placeholder="e.g. 1200" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
          </div>
          <div className="text-right mt-2">
            <button
              type="button"
              className="px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm"
              onClick={async () => {
                  try {
                    const cleaned = aeoRows
                      .map(r => ({
                        name: r.name.trim(),
                        shareOfVoice: Number(String(r.sov).replace(/[^0-9.\-]/g,'')),
                        sentiment: Number(String(r.sentiment).replace(/[^0-9.\-]/g,'')),
                        size: Number(String(r.mentions).replace(/[^0-9.\-]/g,''))
                      }))
                      .filter(p => p.name && Number.isFinite(p.shareOfVoice) && Number.isFinite(p.sentiment));
                    const updated = { ...(currentClientData as any) };
                    const sections = Array.isArray(updated.flexibleSections) ? [...updated.flexibleSections] : [];
                    const idx = sections.findIndex((s: any) => (s?.title || '').toLowerCase().includes('aeo/geo/seo'));
                    if (idx >= 0) {
                      const prevData = sections[idx].data || {};
                      sections[idx] = { ...sections[idx], title: 'AEO/GEO/SEO', data: { ...prevData, points: cleaned } };
                    } else {
                      sections.unshift({ title: 'AEO/GEO/SEO', order: -10, data: { points: cleaned, opportunitiesText: aeoGeoSeoText } });
                    }
                    updated.flexibleSections = sections;
                    setCurrentClientData(updated);
                    if (currentClientData.id) {
                      await ClientService.updateClientData(currentClientData.id, 'flexible_sections', sections);
                      alert('AEO/GEO/SEO points saved');
                    }
                  } catch (e) {
                    console.error(e);
                    alert('Failed to save AEO/GEO/SEO points');
                  }
                }}
              >
                Save Points
              </button>
            </div>
          </div>

          {/* Page Layout Configuration */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xl font-bold text-teal-600">Page Layout Content</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Control the hero areas and highlight sections used on the Overview, SEO/AEO/GEO, and Research pages. Upload images directly or paste a public URL. These settings save together with the main “Save Changes” button below.
            </p>

            {/* Landing Page */}
            <div className="space-y-4 mb-6">
              <h4 className="text-md font-semibold text-gray-900">Landing Page</h4>
              <div className="bg-white border rounded-lg p-4 space-y-4">
                <div>
                  <h5 className="text-sm font-semibold text-gray-800 mb-2">Hero</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={pageContent.landing?.hero?.eyebrow || ''}
                      onChange={(e) => updateLandingHero('eyebrow', e.target.value)}
                      className="w-full px-3 py-2 border rounded"
                      placeholder="Eyebrow e.g. KEY INSIGHT"
                    />
                    <input
                      type="text"
                      value={pageContent.landing?.hero?.title || ''}
                      onChange={(e) => updateLandingHero('title', e.target.value)}
                      className="w-full px-3 py-2 border rounded"
                      placeholder="Hero title"
                    />
                    <input
                      type="text"
                      value={pageContent.landing?.hero?.subtitle || ''}
                      onChange={(e) => updateLandingHero('subtitle', e.target.value)}
                      className="w-full px-3 py-2 border rounded"
                      placeholder="Hero subtitle"
                    />
                    <div className="md:col-span-2">
                      {renderImageInput(
                        pageContent.landing?.hero?.backgroundImage,
                        (val) => updateLandingHero('backgroundImage', val),
                        'Hero background image'
                      )}
                    </div>
                  </div>
                  <textarea
                    value={pageContent.landing?.hero?.description || ''}
                    onChange={(e) => updateLandingHero('description', e.target.value)}
                    className="w-full px-3 py-2 border rounded mt-3"
                    rows={3}
                    placeholder="Hero supporting description"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <input
                      type="text"
                      value={pageContent.landing?.hero?.ctaLabel || ''}
                      onChange={(e) => updateLandingHero('ctaLabel', e.target.value)}
                      className="w-full px-3 py-2 border rounded"
                      placeholder="CTA label"
                    />
                    <input
                      type="text"
                      value={pageContent.landing?.hero?.ctaUrl || ''}
                      onChange={(e) => updateLandingHero('ctaUrl', e.target.value)}
                      className="w-full px-3 py-2 border rounded"
                      placeholder="CTA URL"
                    />
                  </div>
                </div>

                <div>
                  <h5 className="text-sm font-semibold text-gray-800 mb-2">Key Insight Banner</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={pageContent.landing?.keyInsight?.eyebrow || ''}
                      onChange={(e) => updateLandingKeyInsight('eyebrow', e.target.value)}
                      className="w-full px-3 py-2 border rounded"
                      placeholder="Eyebrow"
                    />
                    <input
                      type="text"
                      value={pageContent.landing?.keyInsight?.title || ''}
                      onChange={(e) => updateLandingKeyInsight('title', e.target.value)}
                      className="w-full px-3 py-2 border rounded"
                      placeholder="Headline"
                    />
                  </div>
                  <input
                    type="text"
                    value={pageContent.landing?.keyInsight?.subtitle || ''}
                    onChange={(e) => updateLandingKeyInsight('subtitle', e.target.value)}
                    className="w-full px-3 py-2 border rounded mt-3"
                    placeholder="Subheadline"
                  />
                  <textarea
                    value={pageContent.landing?.keyInsight?.description || ''}
                    onChange={(e) => updateLandingKeyInsight('description', e.target.value)}
                    className="w-full px-3 py-2 border rounded mt-3"
                    rows={3}
                    placeholder="Description"
                  />
                  <div className="mt-3">
                    {renderImageInput(
                      pageContent.landing?.keyInsight?.backgroundImage,
                      (val) => updateLandingKeyInsight('backgroundImage', val),
                      'Key insight background image'
                    )}
                  </div>

                  <div className="mt-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Supporting Metrics</span>
                      <button
                        type="button"
                        onClick={addLandingKeyPoint}
                        className="text-xs text-emerald-600 hover:text-emerald-700"
                      >
                        Add Metric
                      </button>
                    </div>
                    {(pageContent.landing?.keyInsight?.supportingPoints || []).map((point, idx) => (
                      <div key={idx} className="grid grid-cols-7 gap-2 items-center">
                        <input
                          value={point.value}
                          onChange={(e) => updateLandingKeyPoint(idx, 'value', e.target.value)}
                          className="col-span-2 px-3 py-2 border rounded"
                          placeholder="Value"
                        />
                        <input
                          value={point.label}
                          onChange={(e) => updateLandingKeyPoint(idx, 'label', e.target.value)}
                          className="col-span-4 px-3 py-2 border rounded"
                          placeholder="Label"
                        />
                        <button
                          type="button"
                          onClick={() => removeLandingKeyPoint(idx)}
                          className="text-red-500 hover:text-red-700 text-xs"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="text-sm font-semibold text-gray-800">SEO / AEO / GEO Summary Card</h5>
                      <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={uiSettings.landingCards?.seoSummary !== false}
                          onChange={(e) => setUiSettings(prev => ({ ...prev, landingCards: { ...(prev.landingCards || {}), seoSummary: e.target.checked } }))}
                          className="rounded text-teal-600 focus:ring-teal-500"
                        />
                        Show
                      </label>
                    </div>
                    <input
                      type="text"
                      value={pageContent.landing?.seoSummary?.title || ''}
                      onChange={(e) => updateLandingCard('seoSummary', 'title', e.target.value)}
                      className="w-full px-3 py-2 border rounded mb-2"
                      placeholder="Card title"
                    />
                    <textarea
                      value={pageContent.landing?.seoSummary?.body || ''}
                      onChange={(e) => updateLandingCard('seoSummary', 'body', e.target.value)}
                      className="w-full px-3 py-2 border rounded mb-2"
                      rows={3}
                      placeholder="Summary"
                    />
                    <input
                      type="text"
                      value={pageContent.landing?.seoSummary?.ctaLabel || ''}
                      onChange={(e) => updateLandingCard('seoSummary', 'ctaLabel', e.target.value)}
                      className="w-full px-3 py-2 border rounded mb-2"
                      placeholder="CTA label"
                    />
                    <input
                      type="text"
                      value={pageContent.landing?.seoSummary?.ctaUrl || ''}
                      onChange={(e) => updateLandingCard('seoSummary', 'ctaUrl', e.target.value)}
                      className="w-full px-3 py-2 border rounded"
                      placeholder="CTA URL"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="text-sm font-semibold text-gray-800">Research Summary Card</h5>
                      <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={uiSettings.landingCards?.researchSummary !== false}
                          onChange={(e) => setUiSettings(prev => ({ ...prev, landingCards: { ...(prev.landingCards || {}), researchSummary: e.target.checked } }))}
                          className="rounded text-teal-600 focus:ring-teal-500"
                        />
                        Show
                      </label>
                    </div>
                    <input
                      type="text"
                      value={pageContent.landing?.researchSummary?.title || ''}
                      onChange={(e) => updateLandingCard('researchSummary', 'title', e.target.value)}
                      className="w-full px-3 py-2 border rounded mb-2"
                      placeholder="Card title"
                    />
                    <textarea
                      value={pageContent.landing?.researchSummary?.body || ''}
                      onChange={(e) => updateLandingCard('researchSummary', 'body', e.target.value)}
                      className="w-full px-3 py-2 border rounded mb-2"
                      rows={3}
                      placeholder="Summary"
                    />
                    <input
                      type="text"
                      value={pageContent.landing?.researchSummary?.ctaLabel || ''}
                      onChange={(e) => updateLandingCard('researchSummary', 'ctaLabel', e.target.value)}
                      className="w-full px-3 py-2 border rounded mb-2"
                      placeholder="CTA label"
                    />
                    <input
                      type="text"
                      value={pageContent.landing?.researchSummary?.ctaUrl || ''}
                      onChange={(e) => updateLandingCard('researchSummary', 'ctaUrl', e.target.value)}
                      className="w-full px-3 py-2 border rounded"
                      placeholder="CTA URL"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* SEO Page */}
            <div className="space-y-4 mb-6">
              <h4 className="text-md font-semibold text-gray-900">SEO / AEO / GEO Page</h4>
              <div className="bg-white border rounded-lg p-4 space-y-4">
                <div>
                  <h5 className="text-sm font-semibold text-gray-800 mb-2">Hero</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={pageContent.seo?.hero?.eyebrow || ''}
                      onChange={(e) => updateSeoHero('eyebrow', e.target.value)}
                      className="w-full px-3 py-2 border rounded"
                      placeholder="Eyebrow"
                    />
                    <input
                      type="text"
                      value={pageContent.seo?.hero?.title || ''}
                      onChange={(e) => updateSeoHero('title', e.target.value)}
                      className="w-full px-3 py-2 border rounded"
                      placeholder="Title"
                    />
                    <input
                      type="text"
                      value={pageContent.seo?.hero?.subtitle || ''}
                      onChange={(e) => updateSeoHero('subtitle', e.target.value)}
                      className="w-full px-3 py-2 border rounded"
                      placeholder="Subtitle"
                    />
                    <div className="md:col-span-2">
                      {renderImageInput(
                        pageContent.seo?.hero?.backgroundImage,
                        (val) => updateSeoHero('backgroundImage', val),
                        'SEO hero background image'
                      )}
                    </div>
                  </div>
                  <textarea
                    value={pageContent.seo?.hero?.description || ''}
                    onChange={(e) => updateSeoHero('description', e.target.value)}
                    className="w-full px-3 py-2 border rounded mt-3"
                    rows={2}
                    placeholder="Supporting description"
                  />
                </div>

                <div>
                  <h5 className="text-sm font-semibold text-gray-800 mb-2">Intro Copy</h5>
                  <input
                    type="text"
                    value={pageContent.seo?.intro?.title || ''}
                    onChange={(e) => updateSeoIntro('title', e.target.value)}
                    className="w-full px-3 py-2 border rounded mb-2"
                    placeholder="Intro title"
                  />
                  <textarea
                    value={pageContent.seo?.intro?.body || ''}
                    onChange={(e) => updateSeoIntro('body', e.target.value)}
                    className="w-full px-3 py-2 border rounded mb-2"
                    rows={3}
                    placeholder="Introductory paragraph"
                  />
                  <input
                    type="text"
                    value={pageContent.seo?.intro?.ctaLabel || ''}
                    onChange={(e) => updateSeoIntro('ctaLabel', e.target.value)}
                    className="w-full px-3 py-2 border rounded mb-2"
                    placeholder="CTA label"
                  />
                  <input
                    type="text"
                    value={pageContent.seo?.intro?.ctaUrl || ''}
                    onChange={(e) => updateSeoIntro('ctaUrl', e.target.value)}
                    className="w-full px-3 py-2 border rounded"
                    placeholder="CTA URL"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="text-sm font-semibold text-gray-800">Content Topics</h5>
                    <button
                      type="button"
                      onClick={addContentTopic}
                      className="text-xs text-emerald-600 hover:text-emerald-700"
                    >
                      Add Topic
                    </button>
                  </div>
                  {(pageContent.seo?.contentTopics || []).map((topic, idx) => (
                    <div key={topic.id || idx} className="border border-dashed border-gray-300 rounded-lg p-4 mb-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs uppercase tracking-[0.3em] text-gray-500">{idx === 0 ? 'Sample Topics' : `Topic ${idx + 1}`}</span>
                        {pageContent.seo?.contentTopics && pageContent.seo.contentTopics.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeContentTopic(idx)}
                            className="text-xs text-red-500 hover:text-red-600"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <input
                        type="text"
                        value={topic.title}
                        onChange={(e) => updateContentTopic(idx, 'title', e.target.value)}
                        className="w-full px-3 py-2 border rounded"
                        placeholder="Topic title"
                      />
                      <textarea
                        value={topic.description || ''}
                        onChange={(e) => updateContentTopic(idx, 'description', e.target.value)}
                        className="w-full px-3 py-2 border rounded"
                        rows={2}
                        placeholder="Topic description"
                      />
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500 uppercase tracking-[0.2em]">Bullet Points</span>
                          <button
                            type="button"
                            onClick={() => addContentTopicBullet(idx)}
                            className="text-xs text-emerald-600 hover:text-emerald-700"
                          >
                            Add Bullet
                          </button>
                        </div>
                        {(topic.bullets || []).map((bullet, bulletIdx) => (
                          <div key={bulletIdx} className="flex items-start gap-2">
                            <input
                              type="text"
                              value={bullet}
                              onChange={(e) => updateContentTopicBullet(idx, bulletIdx, e.target.value)}
                              className="flex-1 px-3 py-2 border rounded"
                              placeholder="Bullet detail"
                            />
                            <button
                              type="button"
                              onClick={() => removeContentTopicBullet(idx, bulletIdx)}
                              className="text-xs text-red-500 hover:text-red-600"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div>
                  <h5 className="text-sm font-semibold text-gray-800 mb-2">Additional Notes</h5>
                  <textarea
                    value={pageContent.seo?.notes || ''}
                    onChange={(e) => updateSeoNotes(e.target.value)}
                    className="w-full px-3 py-2 border rounded"
                    rows={3}
                    placeholder="Optional notes displayed below content topics"
                  />
                </div>
              </div>
            </div>

            {/* Research Page */}
            <div className="space-y-4">
              <h4 className="text-md font-semibold text-gray-900">Research Insights Page</h4>
              <div className="bg-white border rounded-lg p-4 space-y-4">
                <div>
                  <h5 className="text-sm font-semibold text-gray-800 mb-2">Hero</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={pageContent.research?.hero?.eyebrow || ''}
                      onChange={(e) => updateResearchHero('eyebrow', e.target.value)}
                      className="w-full px-3 py-2 border rounded"
                      placeholder="Eyebrow"
                    />
                    <input
                      type="text"
                      value={pageContent.research?.hero?.title || ''}
                      onChange={(e) => updateResearchHero('title', e.target.value)}
                      className="w-full px-3 py-2 border rounded"
                      placeholder="Title"
                    />
                    <input
                      type="text"
                      value={pageContent.research?.hero?.subtitle || ''}
                      onChange={(e) => updateResearchHero('subtitle', e.target.value)}
                      className="w-full px-3 py-2 border rounded"
                      placeholder="Subtitle"
                    />
                    <div className="md:col-span-2">
                      {renderImageInput(
                        pageContent.research?.hero?.backgroundImage,
                        (val) => updateResearchHero('backgroundImage', val),
                        'Research hero background image'
                      )}
                    </div>
                  </div>
                  <textarea
                    value={pageContent.research?.hero?.description || ''}
                    onChange={(e) => updateResearchHero('description', e.target.value)}
                    className="w-full px-3 py-2 border rounded mt-3"
                    rows={2}
                    placeholder="Supporting description"
                  />
                </div>

                <div>
                  <h5 className="text-sm font-semibold text-gray-800 mb-2">Intro Copy</h5>
                  <input
                    type="text"
                    value={pageContent.research?.intro?.title || ''}
                    onChange={(e) => updateResearchIntro('title', e.target.value)}
                    className="w-full px-3 py-2 border rounded mb-2"
                    placeholder="Intro title"
                  />
                  <textarea
                    value={pageContent.research?.intro?.body || ''}
                    onChange={(e) => updateResearchIntro('body', e.target.value)}
                    className="w-full px-3 py-2 border rounded mb-2"
                    rows={3}
                    placeholder="Introductory paragraph"
                  />
                  <input
                    type="text"
                    value={pageContent.research?.intro?.ctaLabel || ''}
                    onChange={(e) => updateResearchIntro('ctaLabel', e.target.value)}
                    className="w-full px-3 py-2 border rounded mb-2"
                    placeholder="CTA label"
                  />
                  <input
                    type="text"
                    value={pageContent.research?.intro?.ctaUrl || ''}
                    onChange={(e) => updateResearchIntro('ctaUrl', e.target.value)}
                    className="w-full px-3 py-2 border rounded"
                    placeholder="CTA URL"
                  />
                </div>
              </div>
            </div>

            {/* Additional Insights */}
            <div className="space-y-4 mt-6">
              <h4 className="text-md font-semibold text-gray-900">Additional Insights</h4>
              <div className="bg-white border rounded-lg p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Top Title</label>
                  <input
                    type="text"
                    value={pageContent.generalInfo?.topTitle || ''}
                    onChange={(e) => updateGeneralInfo('topTitle', e.target.value)}
                    className="w-full px-3 py-2 border rounded mb-2"
                    placeholder="Top Title"
                  />
                  <label className="block text-sm font-medium text-gray-700 mb-1">Top Title Subhead</label>
                  <input
                    type="text"
                    value={pageContent.generalInfo?.topTitleSubhead || ''}
                    onChange={(e) => updateGeneralInfo('topTitleSubhead', e.target.value)}
                    className="w-full px-3 py-2 border rounded mb-2"
                    placeholder="Top Title Subhead"
                  />
                  <label className="block text-sm font-medium text-gray-700 mb-1">Headline</label>
                  <input
                    type="text"
                    value={pageContent.generalInfo?.headline || ''}
                    onChange={(e) => updateGeneralInfo('headline', e.target.value)}
                    className="w-full px-3 py-2 border rounded mb-2"
                    placeholder="Headline"
                  />
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subhead</label>
                  <input
                    type="text"
                    value={pageContent.generalInfo?.subhead || ''}
                    onChange={(e) => updateGeneralInfo('subhead', e.target.value)}
                    className="w-full px-3 py-2 border rounded mb-2"
                    placeholder="Subhead"
                  />
                  <label className="block text-sm font-medium text-gray-700 mb-1">Body</label>
                  <RichTextEditor
                    value={pageContent.generalInfo?.mainContent || ''}
                    onChange={(val) => updateGeneralInfo('mainContent', val)}
                    placeholder="Body"
                    className="mt-1"
                    onUploadAsset={handleRichTextAssetUpload}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Visibility Settings</h3>

            <h4 className="text-sm font-semibold text-gray-900 mb-2">Navigation Menu</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 text-sm mb-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={uiSettings.nav?.overview !== false}
                  onChange={(e) => setUiSettings(prev => ({ ...prev, nav: { ...(prev.nav || {}), overview: e.target.checked } }))}
                />
                Overview
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={uiSettings.nav?.seo !== false}
                  onChange={(e) => setUiSettings(prev => ({ ...prev, nav: { ...(prev.nav || {}), seo: e.target.checked } }))}
                />
                SEO / AEO / GEO
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={uiSettings.nav?.research !== false}
                  onChange={(e) => setUiSettings(prev => ({ ...prev, nav: { ...(prev.nav || {}), research: e.target.checked } }))}
                />
                Research Insights
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={uiSettings.nav?.generalInfo === true}
                  onChange={(e) => setUiSettings(prev => ({ ...prev, nav: { ...(prev.nav || {}), generalInfo: e.target.checked } }))}
                />
                Additional Insights
              </label>
            </div>

            <h4 className="text-sm font-semibold text-gray-900 mb-2">Sections & Charts</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={uiSettings.legacyCharts?.demographics !== false} onChange={(e)=>setUiSettings(prev=>({ ...prev, legacyCharts: { ...(prev.legacyCharts||{}), demographics: e.target.checked } }))} />
                Demographics
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={uiSettings.legacyCharts?.personalValues !== false} onChange={(e)=>setUiSettings(prev=>({ ...prev, legacyCharts: { ...(prev.legacyCharts||{}), personalValues: e.target.checked } }))} />
                Personal Values
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={uiSettings.legacyCharts?.psychologicalDrivers !== false} onChange={(e)=>setUiSettings(prev=>({ ...prev, legacyCharts: { ...(prev.legacyCharts||{}), psychologicalDrivers: e.target.checked } }))} />
                Psychological Drivers
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={uiSettings.legacyCharts?.hobbiesAndHabits !== false} onChange={(e)=>setUiSettings(prev=>({ ...prev, legacyCharts: { ...(prev.legacyCharts||{}), hobbiesAndHabits: e.target.checked } }))} />
                Hobbies &amp; Habits
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={uiSettings.legacyCharts?.dailyRoutine !== false} onChange={(e)=>setUiSettings(prev=>({ ...prev, legacyCharts: { ...(prev.legacyCharts||{}), dailyRoutine: e.target.checked } }))} />
                Daily Routine
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={uiSettings.legacyCharts?.brandAffinity !== false} onChange={(e)=>setUiSettings(prev=>({ ...prev, legacyCharts: { ...(prev.legacyCharts||{}), brandAffinity: e.target.checked } }))} />
                Brand Affinity
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={uiSettings.sections?.['Site Affinity'] !== false} onChange={(e)=>setUiSettings(prev=>({ ...prev, sections: { ...(prev.sections||{}), ['Site Affinity']: e.target.checked } }))} />
                Site Affinity
              </label>
              
            </div>
            <div className="mt-3 text-xs text-gray-600">These settings are saved with the primary “Save Changes” button.</div>

            <h4 className="text-sm font-semibold text-gray-900 mt-4 mb-2">Theme Colors</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm items-center">
              <label className="flex items-center gap-2">
                <span className="w-24">Primary</span>
                <input type="color" value={uiSettings.theme?.primary || '#3E72B5'} onChange={(e)=>setUiSettings(prev=>({ ...prev, theme: { ...(prev.theme||{}), primary: e.target.value } }))} />
                <input type="text" value={uiSettings.theme?.primary || ''} onChange={(e)=>setUiSettings(prev=>({ ...prev, theme: { ...(prev.theme||{}), primary: e.target.value } }))} className="ml-2 px-2 py-1 border rounded w-28" />
              </label>
              <label className="flex items-center gap-2">
                <span className="w-24">Secondary</span>
                <input type="color" value={uiSettings.theme?.secondary || '#FDBA19'} onChange={(e)=>setUiSettings(prev=>({ ...prev, theme: { ...(prev.theme||{}), secondary: e.target.value } }))} />
                <input type="text" value={uiSettings.theme?.secondary || ''} onChange={(e)=>setUiSettings(prev=>({ ...prev, theme: { ...(prev.theme||{}), secondary: e.target.value } }))} className="ml-2 px-2 py-1 border rounded w-28" />
              </label>
              <label className="flex items-center gap-2">
                <span className="w-24">Background</span>
                <input type="color" value={(uiSettings.theme as any)?.background || '#99999'} onChange={(e)=>setUiSettings(prev=>({ ...prev, theme: { ...(prev.theme||{} as any), background: e.target.value } }))} />
                <input type="text" value={(uiSettings.theme as any)?.background || ''} onChange={(e)=>setUiSettings(prev=>({ ...prev, theme: { ...(prev.theme||{} as any), background: e.target.value } }))} className="ml-2 px-2 py-1 border rounded w-28" />
              </label>
            </div>
          </div>

          
        </form>

        {/* Sticky footer with actions and debug */}
        <div className="sticky bottom-0 bg-gray-50 border-t p-6 space-y-3">
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="admin-form"
                disabled={isSaving}
                className="px-4 py-2 bg-teal-600 text-white rounded-md text-sm font-medium flex items-center hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowDebug((prev) => !prev)}
              className="text-sm text-gray-500 hover:text-gray-700 ml-auto"
            >
              {showDebug ? 'Hide Debug' : 'Show Debug'}
            </button>
          </div>

          {showDebug && (
            <div className="bg-white border rounded p-4 text-xs text-gray-600 font-mono max-h-48 overflow-y-auto">
              <pre>{JSON.stringify(currentClientData, null, 2)}</pre>
            </div>
          )}

          <details className="group max-w-[600px] w-full mx-auto">
            <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900 flex items-center">
              <span>🐛 Debug: OpenAI Output</span>
              <svg className="ml-2 h-4 w-4 transform group-open:rotate-90 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </summary>

            <div className="mt-4 space-y-4">
              {/* Last OpenAI Output */}
              {lastOpenAIOutput ? (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    🤖 Last OpenAI Output ({lastOpenAIOutput.timestamp}):
                  </h4>
                  <div className="bg-blue-50 border border-blue-200 rounded p-3">
                    <div className="text-xs font-medium text-blue-800 mb-2">
                      Data Type: {lastOpenAIOutput.dataType}
                    </div>
                    <pre className="bg-white border rounded p-3 text-xs overflow-auto max-h-60 text-gray-600">
                      {JSON.stringify(lastOpenAIOutput.data, null, 2)}
                    </pre>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-gray-600">No OpenAI output yet. Upload and analyze files to see results here.</div>
              )}

              {/* Current Client Data */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Current Client Data:</h4>
                <pre className="bg-white border rounded p-3 text-xs overflow-auto max-h-40 text-gray-600">
                  {JSON.stringify({
                    id: currentClientData.id,
                    flexibleSections: currentClientData.flexibleSections,
                    insights: currentClientData.insights,
                    customCharts: currentClientData.customCharts
                  }, null, 2)}
                </pre>
              </div>

              {/* Raw Database Data */}
              <div>
                <button
                  onClick={async () => {
                    if (currentClientData.id) {
                      try {
                        const dbData = await ClientService.getClientData(currentClientData.id);
                        console.log('Raw database data:', dbData);
                        alert('Database data logged to console');
                      } catch (error) {
                        console.error('Error fetching database data:', error);
                        alert('Error fetching database data - check console');
                      }
                    }
                  }}
                  className="text-xs bg-gray-600 text-white px-3 py-1 rounded hover:bg-gray-700"
                >
                  Log Database Data to Console
                </button>
              </div>

              {/* Test OpenAI Connection + Refresh */}
              <div>
                <button
                  onClick={async () => {
                    try {
                      const { extractDataFromFile } = await import('../lib/openai');
                      const testResult = await extractDataFromFile('Test data for OpenAI connection', 'test');
                      console.log('OpenAI test result:', testResult);
                      alert('OpenAI test completed - check console');
                    } catch (error) {
                      console.error('OpenAI test error:', error);
                      alert(`OpenAI test failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
                    }
                  }}
                  className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 mr-2"
                >
                  Test OpenAI Connection
                </button>

                <button
                  onClick={async () => {
                    try {
                      if (currentClientData.id) {
                        console.log('🔄 Refreshing client data from database...');
                        const refreshedData = await ClientService.getClientBySlug(currentClientData.slug);
                        console.log('🔄 Refreshed data:', refreshedData);
                        if (refreshedData) {
                          setCurrentClientData(refreshedData);
                          alert('Client data refreshed - check if charts appear now');
                        } else {
                          alert('Failed to refresh data: Client not found.');
                        }
                      }
                    } catch (error) {
                      console.error('Refresh error:', error);
                      alert(`Refresh failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
                    }
                  }}
                  className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                >
                  🔄 Refresh Client Data
                </button>
              </div>
            </div>
          </details>
        </div>
      </div>

      {/* File Upload Modal */}
      {showFileUpload && currentClientData.id && (
        <FileUpload
          clientId={currentClientData.id}
          onDataExtracted={handleDataExtracted}
          onClose={() => setShowFileUpload(false)}
          analysisHint={uploadHint}
          audienceId={selectedAudienceId}
        />
      )}
    </div>
  );
}
